import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import InstitutionNavbar from '../components/instituteNavbar';
import Footer from '../components/Footer';
import '../styles/GlobalStyles.css';
import { FaRecycle } from 'react-icons/fa';

const ManageJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/getMyJobs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJobs(response.data.data || []);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setError('Failed to load jobs.');
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const handleEdit = (jobId) => {
    navigate(`/post-job?id=${jobId}&mode=edit`);
  };

  const handleView = (jobId) => {
    navigate(`/view-job/${jobId}`);
  };

  const handleDelete = async (jobId) => {
    if (window.confirm("Are you sure you want to delete this job? This action cannot be undone.")) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/institutionfilter/deleteJob/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJobs(jobs.filter(job => job._id !== jobId));
        // Optional: show a toast notification
      } catch (err) {
        console.error("Error deleting job:", err);
        alert("Failed to delete job.");
      }
    }
  };

  const handlePostJobClick = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/getInstProfile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data && res.data.Institute) {
        const { name, type, address } = res.data.Institute;
        // Check strict address
        const isBengaluru = address?.city?.toLowerCase().includes('bengaluru') || address?.city?.toLowerCase().includes('bangalore');

        const complete = name && type && address &&
          address.street &&
          address.city &&
          address.state &&
          address.postal_code &&
          address.country &&
          (!isBengaluru || address.area);

        if (complete) {
          navigate('/post-job');
        } else {
          if (window.confirm("Update your profile before posting a job.\n(Name, Type, and Address fields are required)\n\nClick OK to Edit Profile.")) {
            navigate('/InstitutionProfile');
          }
        }
      } else {
        // Fallback if no data
        navigate('/post-job');
      }
    } catch (err) {
      console.error("Error validating profile:", err);
      navigate('/post-job');
    } finally {
      setLoading(false);
    }
  };

  // Logic to process jobs for display
  // 1. We want to show the ORIGINAL job (parent) but with status/link of the NEW job (child) if reposted.
  // 2. We filter out the "Child" jobs from the main list so they don't appear as duplicates at the top.

  const visibleJobs = jobs.filter(job => !job.repost_of_id); // Only parents (or stand-alone jobs)

  // Create a map of Child Jobs keyed by their ID usually, but here we need to find the child GIVEN the parent.
  // The parent has 'reposted_as_id'. So we can look up the child job using that ID.
  const jobMap = jobs.reduce((acc, job) => {
    acc[job._id] = job;
    return acc;
  }, {});


  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <InstitutionNavbar />

      <main
        className="page-container mt-[120px] min-h-screen overflow-visible"
      >


        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Manage Jobs</h1>
          <button
            onClick={handlePostJobClick}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Checking...' : '+ Post New Job'}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-10">Loading jobs...</div>
        ) : error ? (
          <div className="text-center text-red-600 py-10">{error}</div>
        ) : visibleJobs.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-lg shadow">
            <p className="text-gray-500 mb-4">You haven't posted any jobs yet.</p>
            <button
              onClick={handlePostJobClick}
              disabled={loading}
              className="text-blue-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Checking...' : 'Get started by posting a job'}
            </button>
          </div>
        ) : (

          <>
            <div className="hidden md:block bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="p-4 font-semibold text-gray-600">
                        Job Title
                      </th>
                      <th className="p-4 font-semibold text-gray-600">
                        Posted Date
                      </th>
                      <th className="p-4 font-semibold text-gray-600">
                        Closing Date
                      </th>
                      <th className="p-4 font-semibold text-gray-600">
                        Status
                      </th>
                      <th className="p-4 font-semibold text-gray-600">
                        Applications
                      </th>
                      <th className="p-4 font-semibold text-gray-600 text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {visibleJobs.map((job) => {
                      // Check for repost chain - find the latest descendant
                      let displayJob = job;
                      let latestChild = null;

                      let current = job;
                      while (current.reposted_as_id && jobMap[current.reposted_as_id]) {
                        current = jobMap[current.reposted_as_id];
                        latestChild = current;
                      }
                      displayJob = current;

                      const effectiveClosingDate = displayJob.closing_date || job.closing_date;
                      const isClosed = effectiveClosingDate && new Date(effectiveClosingDate) < new Date();

                      // For actions: View/Repost should go to the latest job IDs
                      const viewTargetId = displayJob._id;


                      return (
                        <tr
                          key={job._id}
                          className="hover:bg-gray-50 transition"
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-bold text-gray-800">
                                  {job.title}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {job.address.area}, {job.address.city},{' '}
                                  {job.address.state}
                                </p>
                              </div>
                              {/* Show recycle icon if it has been reposted */}
                              {latestChild && (
                                <FaRecycle className="text-green-600 text-lg" title="Reposted Job" />
                              )}
                            </div>
                          </td>

                          <td className="p-4 text-gray-600">
                            {new Date(
                              job.posted_date
                            ).toLocaleDateString()}
                          </td>

                          <td className="p-4 text-gray-600">
                            {new Date(
                              effectiveClosingDate
                            ).toLocaleDateString()}
                          </td>

                          <td className="p-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${isClosed || displayJob.status !== 'open'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-green-100 text-green-700'
                                }`}
                            >
                              {/* If child exists, it's effectively OPEN (unless child is closed) */}
                              {isClosed ? 'CLOSED' : (displayJob.status?.toUpperCase() || 'OPEN')}
                            </span>
                          </td>

                          <td className="p-4 text-gray-600">
                            <button
                              onClick={() => navigate(`/job-applicants/${viewTargetId}`)}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              Applicants: {displayJob.applicants?.length || 0}
                            </button>
                          </td>

                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              {/* Show Repost button if the LATEST job is Closed */}
                              {isClosed && (
                                <button
                                  onClick={() => navigate(`/post-job?id=${viewTargetId}&mode=repost`)}
                                  className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition text-sm font-semibold"
                                >
                                  Repost
                                </button>
                              )}

                              <button
                                onClick={() => handleView(viewTargetId)}
                                className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition text-sm font-semibold"
                              >
                                View
                              </button>
                              {/* Show Edit button only for open jobs */}
                              {!isClosed && (
                                <button
                                  onClick={() => handleEdit(viewTargetId)}
                                  className="border border-blue-300 text-blue-600 bg-blue-50 px-3 py-1 rounded hover:bg-blue-100 transition text-sm font-semibold"
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(job._id)}
                                className="text-red-500 hover:text-red-700 font-medium text-sm border border-red-200 bg-red-50 px-3 py-1 rounded hover:bg-red-100 transition"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="md:hidden space-y-4">
              {visibleJobs.map((job) => {
                let displayJob = job;
                let latestChild = null;

                let current = job;
                while (current.reposted_as_id && jobMap[current.reposted_as_id]) {
                  current = jobMap[current.reposted_as_id];
                  latestChild = current;
                }
                displayJob = current;

                const effectiveClosingDate = displayJob.closing_date || job.closing_date;
                const isClosed = effectiveClosingDate && new Date(effectiveClosingDate) < new Date();
                const viewTargetId = displayJob._id;

                return (
                  <div
                    key={job._id}
                    className="bg-white rounded-lg shadow p-4"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h2 className="text-lg font-bold text-gray-800">
                          {job.title}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          {job.address.area}, {job.address.city},{' '}
                          {job.address.state}
                        </p>
                      </div>
                      {latestChild && (
                        <FaRecycle className="text-green-600 text-xl" title="Reposted Job" />
                      )}
                    </div>


                    <div className="flex justify-between text-sm text-gray-600 mt-3">
                      <span>
                        {new Date(
                          job.posted_date
                        ).toLocaleDateString()}
                      </span>

                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${isClosed || displayJob.status !== 'open'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                          }`}
                      >
                        {/* Logic refinement: If it has a child, show normal status of child (Open) */}
                        {isClosed ? 'CLOSED' : (displayJob.status?.toUpperCase() || 'OPEN')}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 mt-2">
                      Applicants: {displayJob.applicants?.length || 0}
                    </p>

                    <div className="flex gap-3 mt-4">
                      {isClosed && (
                        <button
                          onClick={() => navigate(`/post-job?id=${viewTargetId}&mode=repost`)}
                          className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-semibold"
                        >
                          Repost
                        </button>
                      )}
                      <button
                        onClick={() => handleView(viewTargetId)}
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold"
                      >
                        View
                      </button>

                      <button
                        onClick={() => handleEdit(job._id)}
                        className="flex-1 border border-gray-300 py-2 rounded-lg text-sm font-semibold"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(job._id)}
                        className="flex-1 bg-red-50 text-red-600 border border-red-200 py-2 rounded-lg text-sm font-semibold hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ManageJobs;